import React, { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router-dom";

export default function FeaturedProjects({ title, data }) {
  const isMobile = useMediaQuery({ maxWidth: 425 });
  const sliderRef = useRef(null);

  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNextClick = (e) => {
    if (currentIndex !== data?.length - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
    } else {
      setCurrentIndex(data?.length - 1);
    }
    e.preventDefault();
    if (sliderRef.current) {
      if (isMobile) {
        if (currentIndex + 1 >= data?.length) {
          return;
        }
        // Scroll to the next child element
        sliderRef.current.children[currentIndex + 1].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start",
        });
      } else {
        // Scroll by 336px for larger screens
        sliderRef.current.scrollBy({
          left: 336,
          behavior: "smooth",
        });
      }
    }
  };
  const handlePrevClick = (e) => {
    if (currentIndex > 0) {
      setCurrentIndex((prevIndex) => prevIndex - 1);
    } else {
      setCurrentIndex(0);
    }
    e.preventDefault();
    if (sliderRef.current) {
      if (isMobile) {
        if (currentIndex - 1 < 0) {
          return;
        }
        // Scroll to the previous child element
        sliderRef.current.children[currentIndex - 1].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start",
        });
      } else {
        // Scroll by 336px for larger screens
        sliderRef.current.scrollBy({
          left: -328,
          behavior: "smooth",
        });
      }
    }
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const isDownRef = { current: false };
    let startX;
    let scrollLeft;

    const handleMouseDown = (e) => {
      isDownRef.current = true;
      slider.classList.add("cursor-grabbing");
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    };

    const handleMouseMove = (e) => {
      if (!isDownRef.current) return;
      const x = e.pageX - slider.offsetLeft;
      const walk = x - startX;
      slider.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUpOrLeave = () => {
      isDownRef.current = false;
      slider.classList.remove("cursor-grabbing");
    };

    slider.addEventListener("mousedown", handleMouseDown);
    slider.addEventListener("mousemove", handleMouseMove);
    slider.addEventListener("mouseup", handleMouseUpOrLeave);
    slider.addEventListener("mouseleave", handleMouseUpOrLeave);

    return () => {
      slider.removeEventListener("mousedown", handleMouseDown);
      slider.removeEventListener("mousemove", handleMouseMove);
      slider.removeEventListener("mouseup", handleMouseUpOrLeave);
      slider.removeEventListener("mouseleave", handleMouseUpOrLeave);
    };
  }, []);

  return (
    <div className="flex flex-col gap-4 ">
      {/* Titile and Arrows */}
      <div className="flex flex-row items-center justify-between">
        <h2 className="font-[optima-lt-pro] font-[500] uppercase text-xl xs:text-xl md:text-3xl 2xl:text-4xl">
          {title}
        </h2>
        <div className="flex flex-row">
          {/* Prev */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className={`size-6 xl:size-8 ${
              currentIndex === 0 ? "text-black" : "text-bhagwa"
            } `}
            onClick={handlePrevClick}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5 8.25 12l7.5-7.5"
            />
          </svg>

          {/* Next */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className={`size-6 xl:size-8 ${
              currentIndex >= 0 && currentIndex < data?.length - 1
                ? "text-bhagwa"
                : "text-black"
            } `}
            onClick={handleNextClick}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m8.25 4.5 7.5 7.5-7.5 7.5"
            />
          </svg>
        </div>
      </div>

      {/* Projects */}
      {/* Image and bottom section container */}
      <div
        ref={sliderRef}
        style={{
          scrollbarWidth: "none",
        }}
        className={`flex flex-row flex-nowrap overflow-x-auto gap-4${""}  select-none`}
      >
        {data?.map((item, index) => {
          return (
            <Link
              draggable="false"
              key={index}
              reloadDocument
              to={item?.featured_project_link}
              className={`${
                isMobile ? "w-full shrink-0" : " shrink-0 w-[320px]"
              }  flex flex-col gap-8 cursor-pointer`}
            >
              {/* Image */}
              <div className="w-full h-[200px] md:h-[300px]">
                <img
                  src={item?.featured_project_image?.url}
                  alt={item?.featured_project_image?.alternativeText}
                  className="w-full h-full object-cover object-center"
                  draggable="false"
                />
              </div>

              {/* Bottom Section - Details */}
              <div>
                <div className="flex flex-col gap-1">
                  <div className="flex flex-row items-center justify-between uppercase">
                    <p className="text-xs md:text-sm  text-gray-500 font-[lato] line-clamp-1">
                      {item?.featured_project_developer}
                    </p>
                    <p className="text-bhagwa text-xs md:text-sm font-[lato] uppercase line-clamp-1">
                      {item?.featured_project_price}
                    </p>
                  </div>
                  <h2 className="text-base md:text-lg uppercase font-normal font-[optima-lt-pro] line-clamp-1">
                    {item?.featured_project_name}
                  </h2>
                </div>
                <div className="flex items-center mt-1  space-x-2 text-sm md:text-base text-gray-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="size-4 md:size-4 xl:size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                    />
                  </svg>

                  <span className="font-[lato] text-xs md:text-sm line-clamp-1 uppercase">
                    {item?.featured_project_location}
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
